import { cloneDeep } from 'es-toolkit';
import { core } from '@tock/coretypes';
import { toggleArrayValues } from 'utils/toggleArrayValues';
import AppConstants from 'constants/AppConstants';

type EditedStatus = 'ALL' | 'YES' | 'NO';
export interface Filter {
  isUnansweredFilterOn: boolean;
  isUnfiredFilterOn: boolean;
  isAmountDueFilterOn: boolean;
  editedStatus: EditedStatus;
  exclusives: core.enums.Exclusivity[];
  partyStatuses: string[];
  purchaseState: core.TicketGroup.PurchaseState[];
  service: number[];
  // size is capped at 10+
  size: number[];
  ticketOptionId: number[];
  ticketTypeId: number[];
  seatingOptionsEnum: string[];
  visitTag: string[];
  guestTag: string[];
}

// Type guard to check if a string is a valid edited status
export const isEditedStatus = (value: string): value is EditedStatus =>
  ['ALL', 'YES', 'NO'].includes(value);

const emptyQueryParams: Filter = {
  isUnansweredFilterOn: false,
  isUnfiredFilterOn: false,
  isAmountDueFilterOn: false,
  editedStatus: 'ALL',
  exclusives: [],
  partyStatuses: [],
  purchaseState: [],
  seatingOptionsEnum: [],
  service: [],
  size: [],
  ticketOptionId: [],
  ticketTypeId: [],
  visitTag: [],
  guestTag: [],
};

export interface State {
  hasFiltersApplied: boolean;
  queryParams: Filter;
}

const initialState: State = {
  hasFiltersApplied: false,
  queryParams: cloneDeep(emptyQueryParams),
};

export const hasFiltersApplied = (params: Filter) => {
  return !(
    params.ticketTypeId.length === 0 &&
    params.purchaseState.length === 0 &&
    params.size.length === 0 &&
    params.exclusives.length === 0 &&
    params.editedStatus === 'ALL' &&
    params.ticketOptionId.length === 0 &&
    params.guestTag.length === 0 &&
    params.visitTag.length === 0 &&
    params.seatingOptionsEnum.length === 0 &&
    !params.isUnansweredFilterOn &&
    params.partyStatuses.length === 0 &&
    !params.isUnfiredFilterOn &&
    !params.isAmountDueFilterOn
  );
};

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload } = action;

  switch (type) {
    case AppConstants.CALENDAR_FILTER_PARAMS_UPDATE: {
      const queryParams = {
        ...state.queryParams,
        ...payload,
      };

      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_PARTY_SIZE: {
      const queryParams = {
        ...state.queryParams,
        size: payload,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_OFFERINGS: {
      const queryParams = {
        ...state.queryParams,
        ticketTypeId: payload,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_GUEST_TAGS: {
      const modifiedPayload = payload;
      const newTags = toggleArrayValues(state.queryParams.guestTag, modifiedPayload);
      const queryParams = {
        ...state.queryParams,
        guestTag: newTags,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_VISIT_TAGS: {
      const modifiedPayload = payload;
      const newTags = toggleArrayValues(state.queryParams.visitTag, modifiedPayload);
      const queryParams = {
        ...state.queryParams,
        visitTag: newTags,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_SUPPLEMENTS: {
      const queryParams = {
        ...state.queryParams,
        ticketOptionId: payload,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_EDITED_STATUS: {
      const queryParams = {
        ...state.queryParams,
        editedStatus: payload,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.UPDATE_FILTER_BY_STATUS: {
      const queryParams: Filter = {
        ...state.queryParams,
        purchaseState: payload,
      };
      return {
        ...state,
        hasFiltersApplied: hasFiltersApplied(queryParams),
        queryParams,
      };
    }
    case AppConstants.CALENDAR_FILTER_CLEAR:
      return {
        ...state,
        hasFiltersApplied: false,
        queryParams: cloneDeep(emptyQueryParams),
      };
    default:
      return state;
  }
}
